import { Link } from "react-router-dom";

/**
 * The canvas's `CTA + Security` screen.
 *
 * Two bands inside one `<section>`: the closing call, then a security strip
 * divided from it by a hairline. They share a section rather than splitting
 * because the ring and both blooms are positioned against the combined height —
 * the 640px ring is centred on the section's midpoint, which lands on the
 * divider only because the strip is inside.
 */
export default function Cta() {
  return (
    <section
      id="contact"
      className="dc-seam dc-seam-foot"
      style={{
        position: "relative",
        overflow: "clip",
        background: "var(--band)",
        color: "var(--band-ink)",
      }}
    >
      {/* Not parallaxed: it is anchored to the section's midpoint. */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          right: -220,
          width: 640,
          height: 640,
          marginTop: -320,
          border: "1px solid rgba(var(--band-acc-rgb),.18)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />
      <div
        className="site-gutter"
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          paddingTop: 128,
          paddingBottom: 110,
          position: "relative",
          textAlign: "center",
        }}
      >
        <h2
          data-rv=""
          style={{
            margin: "0 0 18px",
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 700,
            fontSize: "clamp(34px, 4.4vw, 60px)",
            letterSpacing: "-0.02em",
          }}
        >
          Ready to aim higher?
        </h2>
        <p
          data-rv=""
          style={{
            margin: "0 auto 36px",
            maxWidth: 540,
            fontSize: 17,
            color: "rgba(var(--band-ink-rgb),.7)",
          }}
        >
          Bring one process to the call. We will configure an agent for it and
          run it live on your data.
        </p>
        <div
          data-rv=""
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          {/* The contact page, not a mail client. A mailto dead-ends anyone
              without a desktop mail app configured, and the page can qualify
              the request. */}
          <Link
            to="/contact"
            className="dc-h-btn-cyan"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              background: "rgb(var(--band-acc-rgb))",
              color: "var(--band-on-acc)",
              padding: "16px 30px",
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 15.5,
              transition: "transform .2s, box-shadow .2s",
            }}
          >
            Book a Demo <span aria-hidden="true">&rarr;</span>
          </Link>
          <Link
            to="/pricing"
            className="dc-h-ghost-light"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              border: "1px solid rgba(var(--band-ink-rgb),.3)",
              color: "var(--band-ink)",
              padding: "16px 30px",
              borderRadius: 999,
              fontWeight: 600,
              fontSize: 15.5,
              transition: "border-color .2s, background .2s",
            }}
          >
            See pricing
          </Link>
        </div>
      </div>

      <div
        style={{
          borderTop: "1px solid rgba(var(--band-line-rgb),.1)",
          position: "relative",
        }}
      >
        {/* Heading beside the copy from tablet up, stacked on a phone
            (global.css, T101026002). */}
        <div
          data-rv=""
          className="home-cta-strip site-gutter"
          style={{ paddingTop: 64, paddingBottom: 64 }}
        >
          <div>
            <div
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 11.5,
                letterSpacing: ".16em",
                textTransform: "uppercase",
                color: "var(--band-acc)",
                marginBottom: 14,
              }}
            >
              <span className="dc-mark" aria-hidden="true" /> Security First
            </div>
            <h3
              style={{
                margin: 0,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: "clamp(20px, 2.2vw, 28px)",
                letterSpacing: "-0.01em",
                lineHeight: 1.25,
              }}
            >
              Your data, encrypted and siloed. Always.
            </h3>
          </div>
          <div>
            <p
              style={{
                margin: "0 0 18px",
                fontSize: 15.5,
                color: "rgba(var(--band-ink-rgb),.68)",
              }}
            >
              Enterprise-grade infrastructure with zero-trust architecture.
              Every byte is encrypted at rest and in transit.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {["AES-256 Encryption", "Role-Based ACL"].map((chip) => (
                <span
                  key={chip}
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 12,
                    border: "1px solid rgba(var(--band-acc-rgb),.35)",
                    color: "var(--band-acc)",
                    borderRadius: 999,
                    padding: "7px 15px",
                  }}
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
